/**
 * Plan & usage panel for the Settings page.
 *
 * Read-only in Phase 1 — there is no self-serve checkout yet, so the CTAs route
 * to pricing/contact and the card says so plainly rather than implying a
 * self-service upgrade that does not exist.
 */
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowUpRight, CreditCard, ExternalLink, TrendingUp } from "lucide-react";
import { format } from "date-fns";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import Progress from "../ui/progress";
import { Skeleton } from "../ui/skeleton";
import { useSubscription } from "../../contexts/SubscriptionContext";
import { formatNumber, getNextPlan, getPlan, upgradeGains } from "../../config/plans";
import { usePlanCatalog } from "../../hooks/usePlanCatalog";
import { useApi, API_ENDPOINTS } from "../../lib/api";
import PlanChangeDialog from "./PlanChangeDialog";

const STATUS_STYLES: Record<string, string> = {
  trial: "bg-blue-100 text-blue-700",
  active: "bg-green-100 text-green-700",
  expired: "bg-red-100 text-red-700",
  cancelled: "bg-gray-100 text-gray-700",
  inactive: "bg-gray-100 text-gray-700",
};

const STATUS_LABELS: Record<string, string> = {
  trial: "Trial",
  active: "Active",
  expired: "Expired",
  cancelled: "Cancelled",
  inactive: "Inactive",
};

const fmtDate = (iso: string | null) => (iso ? format(new Date(iso), "MMM d, yyyy") : "—");

const BillingCard: React.FC = () => {
  const navigate = useNavigate();
  const { subscription, loading } = useSubscription();
  const { catalog } = usePlanCatalog();

  const api = useApi();
  const { subscription: sub, refresh } = useSubscription();
  const [searchParams, setSearchParams] = useSearchParams();
  const [dialogPlan, setDialogPlan] = useState<ReturnType<typeof getPlan>>(undefined);
  const [portalBusy, setPortalBusy] = useState(false);

  const currentPlan = subscription ? getPlan(catalog, subscription.plan) : undefined;
  const nextPlan = subscription ? getNextPlan(catalog, subscription.plan) : undefined;

  // Returning from Stripe Checkout: the webhook is what actually activates the
  // plan, so re-fetch rather than trusting the redirect.
  const billingParam = searchParams.get("billing");
  const planParam = searchParams.get("plan");

  useEffect(() => {
    if (billingParam === "success") {
      refresh();
      const t = setTimeout(refresh, 3000); // webhook may land a moment later
      return () => clearTimeout(t);
    }
  }, [billingParam, refresh]);

  // Deep link from the pricing page: /settings?plan=pro opens the dialog.
  useEffect(() => {
    if (!planParam || !catalog.plans.length) return;
    const p = catalog.plans.find((x) => x.id === planParam);
    if (p) setDialogPlan(p);
    searchParams.delete("plan");
    setSearchParams(searchParams, { replace: true });
  }, [planParam, catalog, searchParams, setSearchParams]);

  const openBillingPortal = async () => {
    setPortalBusy(true);
    try {
      const res = await api.post(API_ENDPOINTS.SUBSCRIPTION_PORTAL, {});
      if (res.data?.url) window.location.href = res.data.url;
    } catch (err: any) {
      window.alert(err.response?.data?.message || "Could not open the billing portal.");
    } finally {
      setPortalBusy(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CreditCard className="h-5 w-5" />
          Plan &amp; Usage
        </CardTitle>
        <CardDescription>Your current plan, limits and usage.</CardDescription>
      </CardHeader>

      <CardContent className="space-y-6">
        {loading || !subscription ? (
          <div className="space-y-3">
            <Skeleton width="w-40" height="h-6" />
            <Skeleton height="h-2.5" />
            <Skeleton height="h-2.5" />
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-2xl font-semibold">{subscription.planName}</span>
              <span
                className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                  STATUS_STYLES[subscription.status] || STATUS_STYLES.inactive
                }`}
              >
                {STATUS_LABELS[subscription.status] || subscription.status}
              </span>
              {subscription.isCustomLimits && (
                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                  Custom limits
                </span>
              )}
            </div>

            {!subscription.trialStarted && subscription.status === "trial" ? (
              <p className="text-sm text-gray-500 italic">
                Your 14-day trial starts when you connect your first page.
              </p>
            ) : (
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Started</p>
                  <p className="font-medium">{fmtDate(subscription.startDate)}</p>
                </div>
                <div>
                  <p className="text-gray-500">
                    {subscription.status === "active" ? "Renews" : "Ends"}
                  </p>
                  <p className="font-medium">{fmtDate(subscription.endDate)}</p>
                </div>
                <div>
                  <p className="text-gray-500">Days remaining</p>
                  <p className="font-medium">
                    {subscription.daysRemaining !== null ? subscription.daysRemaining : "—"}
                  </p>
                </div>
              </div>
            )}

            <div className="space-y-4">
              <div>
                <div className="flex justify-between text-sm mb-1.5">
                  <span className="font-medium">
                    {subscription.usage.comments.period === "total"
                      ? "Comments (trial total)"
                      : "Comments this period"}
                  </span>
                  <span className="text-gray-500">
                    {subscription.usage.comments.limit === null
                      ? "Unlimited"
                      : `${formatNumber(subscription.usage.comments.used)} / ${formatNumber(subscription.usage.comments.limit)}`}
                  </span>
                </div>
                <Progress
                  value={subscription.usage.comments.used}
                  max={subscription.usage.comments.limit}
                  label="Comments used"
                  valueText={`${formatNumber(subscription.usage.comments.used)} comments used`}
                />
                {subscription.usage.comments.resetsAt && (
                  <p className="mt-1.5 text-xs text-gray-500">
                    Resets {fmtDate(subscription.usage.comments.resetsAt)}
                  </p>
                )}
              </div>

              <div>
                <div className="flex justify-between text-sm mb-1.5">
                  <span className="font-medium">Connected pages</span>
                  <span className="text-gray-500">
                    {subscription.usage.pages.limit === null
                      ? "Unlimited"
                      : `${subscription.usage.pages.used} / ${subscription.usage.pages.limit}`}
                  </span>
                </div>
                <Progress
                  value={subscription.usage.pages.used}
                  max={subscription.usage.pages.limit}
                  label="Pages connected"
                  valueText={`${subscription.usage.pages.used} pages connected`}
                />
              </div>
            </div>

            {/* What moving up the ladder actually buys. Every plan has the same
                features, so this is stated as concrete volume gains. */}
            {nextPlan && currentPlan && (
              <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                <div className="flex items-start gap-2">
                  <TrendingUp className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-blue-900">
                      Need more? Move up to {nextPlan.name}
                      {nextPlan.priceUsd !== null && (
                        <span className="font-normal"> — ${nextPlan.priceUsd}/month</span>
                      )}
                    </p>
                    <ul className="mt-2 space-y-1">
                      {upgradeGains(currentPlan, nextPlan).map((gain) => (
                        <li key={gain} className="flex items-center gap-2 text-sm text-blue-800">
                          <ArrowUpRight className="h-3.5 w-3.5 shrink-0" />
                          {gain}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}

            <div className="flex flex-wrap items-center gap-3 pt-2">
              {nextPlan ? (
                <Button onClick={() => setDialogPlan(nextPlan)}>
                  Upgrade to {nextPlan.name}
                </Button>
              ) : (
                <Button onClick={() => navigate("/pricing")}>View plans</Button>
              )}

              {subscription.hasBillingAccount && (
                <Button variant="outline" onClick={openBillingPortal} disabled={portalBusy}>
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Manage billing
                </Button>
              )}

              <Button variant="outline" onClick={() => navigate("/contact")}>
                Contact us
              </Button>
            </div>
            <p className="text-xs text-gray-500">
              {subscription.hasBillingAccount
                ? "Change plan, update your card, download invoices or cancel from the billing portal."
                : subscription.stripeEnabled
                  ? "Pay by card for instant activation, or request a plan and pay cash — we'll activate it for you."
                  : "Card payment isn't available yet — request a plan and we'll contact you to arrange payment."}
            </p>
          </>
        )}
      </CardContent>

      <PlanChangeDialog
        open={!!dialogPlan}
        onOpenChange={(o) => {
          if (!o) {
            setDialogPlan(undefined);
            refresh();
          }
        }}
        plan={dialogPlan ?? null}
        subscription={sub}
      />
    </Card>
  );
};

export default BillingCard;
